import React, { useState, useMemo, useEffect } from 'react';
import { Calendar, Select, Typography, Tag, Card, List, Button, Space, Empty, Badge, Spin } from 'antd';
import { CalendarOutlined, CloseOutlined, EnvironmentOutlined, ToolOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { WorkOrder, Team, maximoApi } from '../lib/maximoApi';
import { 
  Wrench, 
  FlashOff, 
  Wind, 
  Building, 
  Packages 
} from 'iconoir-react';

const { Title, Text } = Typography;

interface WorkOrderCalendarProps {
  teams: Team[];
  onClose: () => void;
}

// Helper function to get team icon and clean name
const getTeamInfo = (team: Team) => {
  const cleanName = team.name.replace(/-TEAM$/, '').replace(/-/g, ' ');
  
  let icon;
  if (team.id.includes('MECH')) {
    icon = <Wrench width={16} height={16} className="text-blue-600" />;
  } else if (team.id.includes('ELEC')) {
    icon = <FlashOff width={16} height={16} className="text-yellow-600" />;
  } else if (team.id.includes('HVAC')) {
    icon = <Wind width={16} height={16} className="text-green-600" />;
  } else if (team.id.includes('FAC')) {
    icon = <Building width={16} height={16} className="text-purple-600" />;
  } else if (team.id.includes('PLUMB')) {
    icon = <Packages width={16} height={16} className="text-indigo-600" />;
  } else {
    icon = <Wrench width={16} height={16} className="text-gray-600" />;
  }
  
  return { cleanName, icon };
};

const getPriorityColor = (priority: number) => {
  if (priority <= 1) return 'red';
  if (priority <= 3) return 'orange';
  return 'blue';
};

const WorkOrderCalendar: React.FC<WorkOrderCalendarProps> = ({ teams, onClose }) => {
  const [selectedTeam, setSelectedTeam] = useState<string>(teams[0]?.id || '');
  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs());
  const [currentMonth, setCurrentMonth] = useState<Dayjs>(dayjs());
  const [teamWorkOrders, setTeamWorkOrders] = useState<Record<string, WorkOrder[]>>({});
  const [loading, setLoading] = useState(false);

  // Load work orders for the current month and selected team
  const loadWorkOrdersForMonth = async (month: Dayjs, teamId: string) => {
    if (!teamId) return;
    
    try {
      setLoading(true);
      console.log(`Loading work orders for team ${teamId} in month ${month.format('YYYY-MM')}`);
      
      // Clear existing data first
      setTeamWorkOrders(prev => ({
        ...prev,
        [teamId]: []
      }));
      
      const allWorkOrders: WorkOrder[] = [];
      
      // Try to get work orders without date filter to see if any exist for this team
      try {
        const res = await maximoApi.getTeamWorkOrders(teamId, { 
          limit: 100 // Get more work orders to cover the month
          // No date filter - get all work orders for this team
        });
        
        // Filter the results to only include work orders from the selected month
        const monthWorkOrders = res.data.filter(wo => {
          const woDate = dayjs(wo.reportDate);
          return woDate.isSame(month, 'month');
        });
        
        allWorkOrders.push(...monthWorkOrders);
        console.log(`Found ${res.data.length} total work orders for team ${teamId}, ${monthWorkOrders.length} in ${month.format('YYYY-MM')}`);
      } catch (error) {
        console.log(`No work orders found for team ${teamId}`);
      }
      
      setTeamWorkOrders(prev => ({
        ...prev,
        [teamId]: allWorkOrders
      }));
      
      console.log(`Loaded ${allWorkOrders.length} work orders for team ${teamId} in ${month.format('YYYY-MM')}`);
    } catch (error) {
      console.error('Error loading work orders for month:', error);
      setTeamWorkOrders(prev => ({
        ...prev,
        [teamId]: []
      }));
    } finally {
      setLoading(false);
    }
  };

  // Load work orders when team or month changes
  useEffect(() => {
    if (selectedTeam) {
      loadWorkOrdersForMonth(currentMonth, selectedTeam);
    }
  }, [selectedTeam, currentMonth]);

  // Get work orders for the selected team grouped by date
  const workOrdersByDate = useMemo(() => {
    const orders = teamWorkOrders[selectedTeam] || [];
    const grouped: Record<string, WorkOrder[]> = {};

    orders.forEach(order => {
      const dateKey = dayjs(order.reportDate).format('YYYY-MM-DD');
      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push(order);
    });

    return grouped;
  }, [selectedTeam, teamWorkOrders]);

  // Get work orders for a specific date
  const getWorkOrdersForDate = (date: Dayjs) => {
    const dateKey = date.format('YYYY-MM-DD');
    return workOrdersByDate[dateKey] || [];
  };

  // Custom date cell renderer
  const dateCellRender = (date: Dayjs) => {
    const workOrders = getWorkOrdersForDate(date);
    if (workOrders.length === 0) return null;

    const isToday = date.isSame(dayjs(), 'day');

    // Group by priority for summary view
    const priorityGroups = workOrders.reduce((acc, wo) => {
      acc[wo.priority] = (acc[wo.priority] || 0) + 1;
      return acc;
    }, {} as Record<number, number>);

    return (
      <div className="h-full">
        <div className="flex flex-wrap gap-0.5 mb-0.5">
          {Object.entries(priorityGroups).map(([priority, count]) => (
            <Badge
              key={priority}
              count={count}
              size="small"
              style={{
                fontSize: '10px',
                minWidth: '16px',
                height: '16px',
                lineHeight: '14px',
                backgroundColor: getPriorityColor(Number(priority)) === 'red' ? '#ff4d4f' :
                               getPriorityColor(Number(priority)) === 'orange' ? '#fa8c16' : '#1890ff'
              }}
            />
          ))}
        </div>
        <div className={`text-xs leading-3 ${isToday ? 'text-white text-opacity-90' : 'text-gray-500'}`} style={{ fontSize: '10px' }}>
          {workOrders.length} WO{workOrders.length !== 1 ? 's' : ''}
        </div>
      </div>
    );
  };

  // Selected date work orders
  const selectedDateWorkOrders = getWorkOrdersForDate(selectedDate);

  const currentTeam = teams.find(t => t.id === selectedTeam);
  const teamInfo = currentTeam ? getTeamInfo(currentTeam) : null;

  return (
    <div className="h-screen bg-white flex">
      {/* Left side - Calendar */}
      <div className="flex-1 p-4 flex flex-col">
        <div className="flex justify-between items-center mb-3 flex-shrink-0">
          <div className="flex items-center gap-3">
            <CalendarOutlined className="text-xl text-primary" />
            <Title level={4} className="mb-0">Work Order Calendar</Title>
            <Select
              value={selectedTeam}
              onChange={(value) => {
                setSelectedTeam(value);
                // Clear existing data when switching teams
                setTeamWorkOrders(prev => ({ ...prev, [value]: [] }));
              }}
              style={{ width: 250 }}
              placeholder="Select a team"
              size="small"
              getPopupContainer={(trigger) => trigger.parentElement || document.body}
              dropdownStyle={{ zIndex: 1060 }}
              loading={loading}
            >
              {teams.map(team => {
                const { cleanName, icon } = getTeamInfo(team);
                const workOrderCount = teamWorkOrders[team.id]?.length || 0;
                return (
                  <Select.Option key={team.id} value={team.id}>
                    <div className="flex items-center gap-2">
                      {icon}
                      <span>{cleanName}</span>
                      {workOrderCount > 0 && (
                        <Badge 
                          count={workOrderCount} 
                          style={{ backgroundColor: '#52c41a', marginLeft: 8 }} 
                        />
                      )}
                    </div>
                  </Select.Option>
                );
              })}
            </Select>
            <Button 
              size="small" 
              type="link" 
              onClick={() => setSelectedDate(dayjs())}
              className="px-2 text-xs ml-4"
            >
              Today
            </Button>
          </div>
          <Button icon={<CloseOutlined />} onClick={onClose} size="small">Close</Button>
        </div>

        <div className="calendar-container flex-1">
          <style>
            {`
              .calendar-container {
                height: 100%;
              }
              
              .calendar-container .ant-picker-calendar {
                height: 100% !important;
              }
              
              .calendar-container .ant-picker-calendar-header {
                padding: 4px 8px !important;
                min-height: 40px !important;
              }
              
              .calendar-container .ant-picker-calendar-mode-switch {
                display: none !important;
              }
              
              .calendar-container .ant-picker-calendar-body {
                height: calc(100% - 40px) !important;
                overflow: hidden !important;
              }
              
              /* Make calendar cells square and responsive */
              .calendar-container .ant-picker-cell {
                width: calc(100% / 7) !important;
                aspect-ratio: 1 !important;
                height: calc(min(calc((100vh - 180px) / 6), calc((100vw - 320px) / 7))) !important;
                max-height: calc((100vw - 320px) / 7) !important;
                min-height: 60px !important;
                vertical-align: top !important;
                position: relative !important;
              }
              
              /* Ensure week header cells are properly sized */
              .calendar-container .ant-picker-calendar-header th {
                height: 32px !important;
                text-align: center !important;
              }
              
              .calendar-container .ant-picker-calendar-date-today {
                background: linear-gradient(135deg, #1890ff, #40a9ff) !important;
                border-color: #1890ff !important;
              }
              
              .calendar-container .ant-picker-calendar-date-today .ant-picker-calendar-date-value {
                color: white !important;
                font-weight: bold !important;
              }
              
              .calendar-container .ant-picker-calendar-date-today .ant-picker-calendar-date-content {
                background: rgba(255, 255, 255, 0.1) !important;
                border-radius: 4px !important;
                padding: 2px !important;
                margin-top: 4px !important;
              }
              
              .calendar-container .ant-picker-calendar-date-today .ant-picker-calendar-date-content > div {
                background: rgba(255, 255, 255, 0.2) !important;
                border-radius: 2px !important;
                padding: 1px 4px !important;
              }
              
              .calendar-container .ant-picker-calendar-date-today .ant-picker-calendar-date-content .text-xs {
                color: rgba(255, 255, 255, 0.9) !important;
              }
              
              .calendar-container .ant-picker-calendar-date-content {
                min-height: 40px !important;
                max-height: 60px !important;
                overflow-y: auto !important;
                padding: 1px !important;
              }
              
              .calendar-container .ant-picker-calendar-date {
                height: 100% !important;
                padding: 2px !important;
              }
              
              .calendar-container .ant-picker-calendar-date-value {
                line-height: 16px !important;
                margin-bottom: 1px !important;
                font-size: 12px !important;
              }
              
              /* Hide year/month mode switcher buttons */
              .calendar-container .ant-picker-calendar-header .ant-picker-calendar-mode-switch button:last-child {
                display: none !important;
              }
              
              /* Responsive adjustments for square cells */
              @media (max-height: 900px) {
                .calendar-container .ant-picker-cell {
                  height: calc(min(calc((100vh - 160px) / 6), calc((100vw - 320px) / 7))) !important;
                  min-height: 50px !important;
                }
                .calendar-container .ant-picker-calendar-date-content {
                  min-height: 30px !important;
                  max-height: 45px !important;
                }
              }
              
              @media (max-height: 700px) {
                .calendar-container .ant-picker-cell {
                  height: calc(min(calc((100vh - 140px) / 6), calc((100vw - 320px) / 7))) !important;
                  min-height: 45px !important;
                }
                .calendar-container .ant-picker-calendar-date-content {
                  min-height: 25px !important;
                  max-height: 35px !important;
                }
              }
              
              @media (max-height: 600px) {
                .calendar-container .ant-picker-cell {
                  height: calc(min(calc((100vh - 120px) / 6), calc((100vw - 320px) / 7))) !important;
                  min-height: 40px !important;
                }
                .calendar-container .ant-picker-calendar-date-content {
                  min-height: 20px !important;
                  max-height: 30px !important;
                }
              }
            `}
          </style>
          <div className="relative">
            {loading && (
              <div className="absolute inset-0 bg-white bg-opacity-50 flex items-center justify-center z-10">
                <Spin size="large" />
              </div>
            )}
            <Calendar
              mode="month"
              value={selectedDate}
              onSelect={(date) => setSelectedDate(date)}
              onPanelChange={(date, mode) => {
                console.log(`Calendar panel changed to: ${date.format('YYYY-MM-DD')}, mode: ${mode}`);
                const newMonth = date.startOf('month');
                if (!newMonth.isSame(currentMonth, 'month')) {
                  setCurrentMonth(newMonth);
                  // Clear existing data when changing months
                  setTeamWorkOrders({});
                }
              }}
              dateCellRender={dateCellRender}
              className="border rounded-lg h-full"
            />
          </div>
        </div>
      </div>

      {/* Right side - Selected date details */}
      <div className="w-72 border-l bg-gray-50 p-4 overflow-y-auto flex-shrink-0">
        <div className="mb-4">
          <Text className="text-gray-500">Selected Date</Text>
          <Title level={4} className="mb-0">
            {selectedDate.format('MMMM D, YYYY')}
          </Title>
          {teamInfo && (
            <div className="flex items-center gap-2 mt-2">
              {teamInfo.icon}
              <Text strong>{teamInfo.cleanName}</Text>
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Spin />
            <Text className="ml-2">Loading work orders...</Text>
          </div>
        ) : selectedDateWorkOrders.length > 0 ? (
          <List
            dataSource={selectedDateWorkOrders}
            renderItem={(workOrder) => (
              <Card 
                size="small" 
                className="mb-3"
                title={
                  <div className="flex justify-between items-center">
                    <Text strong>{workOrder.wonum}</Text>
                    <Tag color={getPriorityColor(workOrder.priority)}>
                      P{workOrder.priority}
                    </Tag>
                  </div>
                }
              >
                <Text className="block mb-2">{workOrder.description}</Text>
                <div className="flex items-center gap-4 text-xs text-gray-500">
                  {workOrder.location && (
                    <span className="flex items-center gap-1">
                      <EnvironmentOutlined />
                      {workOrder.location}
                    </span>
                  )}
                  <span className="flex items-center gap-1">
                    <ToolOutlined />
                    {workOrder.workType}
                  </span>
                </div>
                <div className="mt-2">
                  <Tag color="blue">{workOrder.status}</Tag>
                </div>
              </Card>
            )}
          />
        ) : (
          <Empty
            description={
              <div>
                <div>No work orders scheduled for</div>
                <div><strong>{selectedDate.format('MMM D, YYYY')}</strong></div>
                {teamInfo && <div>Team: <strong>{teamInfo.cleanName}</strong></div>}
              </div>
            }
            className="mt-8"
          />
        )}

        {/* Summary Statistics */}
        <div className="mt-6 p-4 bg-white rounded-lg">
          <Title level={5}>
            {currentMonth.format('MMMM YYYY')} Summary
            {teamInfo && (
              <Text className="text-sm font-normal text-gray-500 ml-2">
                - {teamInfo.cleanName}
              </Text>
            )}
          </Title>
          {loading ? (
            <div className="text-center py-4">
              <Spin size="small" />
            </div>
          ) : (
            <Space direction="vertical" className="w-full">
              <div className="flex justify-between">
                <Text>Total Work Orders:</Text>
                <Text strong>{(teamWorkOrders[selectedTeam] || []).length}</Text>
              </div>
              <div className="flex justify-between">
                <Text>Critical (P1):</Text>
                <Text strong className="text-red-500">
                  {(teamWorkOrders[selectedTeam] || []).filter(wo => wo.priority === 1).length}
                </Text>
              </div>
              <div className="flex justify-between">
                <Text>High (P2-P3):</Text>
                <Text strong className="text-orange-500">
                  {(teamWorkOrders[selectedTeam] || []).filter(wo => wo.priority >= 2 && wo.priority <= 3).length}
                </Text>
              </div>
              <div className="flex justify-between">
                <Text>Low (P4-P5):</Text>
                <Text strong className="text-blue-500">
                  {(teamWorkOrders[selectedTeam] || []).filter(wo => wo.priority >= 4).length}
                </Text>
              </div>
              {(teamWorkOrders[selectedTeam] || []).length === 0 && (
                <Text className="text-gray-400 text-center py-2">
                  No work orders found for this month
                </Text>
              )}
            </Space>
          )}
        </div>
      </div>
    </div>
  );
};

export default WorkOrderCalendar;